



































































































import { Component } from "vue-property-decorator";
import ClientDataSectionDialogComponent from "./ClientDataSectionDialog.vue";
import { ProjectDetails } from "../../../data/models/ProjectDetails";
import { ButtonState } from "@/components/common/StateButton.vue";
import ConfirmationDialogComponent from "@/components/common/ConfirmationDialog.vue";
import AddressPositionFieldComponent from "@/components/common/AddressPositionField.vue";
import { GeoLocation, PickedAddressParts, PickerAddress } from "@/utils/GeocodingService";
import { hasCoordinatesChanged } from "@/utils/UserFormUtils";
import { addressPositionAccess } from "@/permissions/UserPermissions";
import { getAddressCountryFallbacks } from "@/data/repository/ClientRepository";
import { getEffectiveUser } from "@/utils/ViewAs";

@Component({
  components: {
    "confirmation-dialog": ConfirmationDialogComponent,
    "address-position-field": AddressPositionFieldComponent,
  },
})
export default class DamageLocationSectionComponent extends ClientDataSectionDialogComponent {
  buttonState = ButtonState.RED;

  private snapshotCountryCode = "";
  private snapshotZipCode = "";
  private snapshotCity = "";
  private snapshotStreet = "";
  private snapshotHouseNumber = "";
  private snapshotCoordinates: GeoLocation | null = null;

  get copyButtonTitle() {
    return this.$t(
      this.projectDetails.clientData.directOrder
        ? "clientData.copyClient"
        : "clientData.copyPolicyHolder"
    ).toString();
  }

  hasInitialData(): boolean {
    const info = this.projectDetails.clientData.damageLocationInfo;
    return !!(info.countryCode || info.zipCode || info.city || info.street || info.houseNumber);
  }

  takeSnapshot(): void {
    const info = this.projectDetails.clientData.damageLocationInfo;
    this.snapshotCountryCode = info.countryCode ?? "";
    this.snapshotZipCode = info.zipCode ?? "";
    this.snapshotCity = info.city ?? "";
    this.snapshotStreet = info.street ?? "";
    this.snapshotHouseNumber = info.houseNumber ?? "";
    this.snapshotCoordinates = info.coordinates ?? null;
  }

  get hasChanged(): boolean {
    const info = this.projectDetails.clientData.damageLocationInfo;
    return (info.countryCode ?? "") !== this.snapshotCountryCode
      || (info.zipCode ?? "") !== this.snapshotZipCode
      || (info.city ?? "") !== this.snapshotCity
      || (info.street ?? "") !== this.snapshotStreet
      || (info.houseNumber ?? "") !== this.snapshotHouseNumber
      || hasCoordinatesChanged(info.coordinates, this.snapshotCoordinates);
  }

  async show(projectDetails: ProjectDetails) {
    return this.showDialog(
      this.$t("clientData.damageLocation").toString(),
      projectDetails
    );
  }

  async onCopy() {
    const damageLocationInfo = this.projectDetails.clientData
      .damageLocationInfo;
    const policyHolderInfo = this.projectDetails.clientData.policyHolderInfo;

    damageLocationInfo.countryCode = policyHolderInfo.countryCode;
    damageLocationInfo.city = policyHolderInfo.city;
    damageLocationInfo.zipCode = policyHolderInfo.zipCode;
    damageLocationInfo.houseNumber = policyHolderInfo.houseNumber;
    damageLocationInfo.street = policyHolderInfo.street;

    // The whole address was just replaced, so any pin still on the old one is wrong.
    // Clearing it lets the address be positioned afresh rather than silently keeping a
    // position that points at somebody else's building.
    damageLocationInfo.coordinates = null;

    this.updateProjectDetails(this.projectDetails);
  }

  /**
   * Whether geocoding is switched on for this client at all.
   *
   * Reactive: a capability change on the current user must reach this getter without a page
   * refresh (see the lessons in CLAUDE.md).
   */
  get enableGeoEncoding(): boolean {
    return addressPositionAccess(getEffectiveUser());
  }

  /** The damage address as currently entered, for the map affordance. */
  get enteredAddress(): PickerAddress {
    const info = this.projectDetails.clientData.damageLocationInfo;

    return {
      street: info.street,
      houseNumber: info.houseNumber,
      zipCode: info.zipCode,
      city: info.city,
      country: info.countryCode,
      // The damage address rarely states a country, so the lookup filter falls back on the
      // branch running the project and then the company (ticket 2609015).
      countryFallbacks: getAddressCountryFallbacks(
        this.projectDetails.clientData.branch
      ),
    };
  }

  /**
   * The damage address as it was when editing began.
   *
   * A pin placed for the previous address must not decide where the map opens. In view mode
   * nothing can have been edited, so there is nothing to compare against.
   */
  get savedAddress(): PickerAddress | undefined {
    return this.isEditMode
      ? {
        street: this.snapshotStreet,
        houseNumber: this.snapshotHouseNumber,
        zipCode: this.snapshotZipCode,
        city: this.snapshotCity,
        country: this.snapshotCountryCode,
      }
      : undefined;
  }

  /**
   * Fills in the address parts a lookup or the map found, for the ones that were left empty:
   * the postcode and city from the automatic lookup, or the whole address from a pin placed
   * on the map while the address was still empty (ticket 2610025).
   *
   * Only ever fills a blank: a value the user typed stands, even where Google disagrees.
   * `$set` for the same reason as the coordinates: a legacy damage location may not carry the
   * key at all, and Vue 2 cannot track one added by assignment.
   */
  onAddressResolved(parts: PickedAddressParts) {
    const info = this.projectDetails.clientData.damageLocationInfo;

    if (parts.street && !info.street) {
      this.$set(info, "street", parts.street);
    }

    if (parts.houseNumber && !info.houseNumber) {
      this.$set(info, "houseNumber", parts.houseNumber);
    }

    if (parts.zipCode && !info.zipCode) {
      this.$set(info, "zipCode", parts.zipCode);
    }

    if (parts.city && !info.city) {
      this.$set(info, "city", parts.city);
    }

    if (parts.country && !info.countryCode) {
      this.$set(info, "countryCode", parts.country);
    }
  }

  /**
   * Stores a position chosen on the map.
   *
   * `$set` rather than assignment: Vue 2 cannot track a key added to an object after
   * creation, and legacy projects have no `coordinates` on their damage location at all.
   * This is also why the affordance is bound with `:value` and `@input` instead of
   * `v-model`.
   */
  onCoordinatesPicked(coordinates: GeoLocation) {
    this.$set(
      this.projectDetails.clientData.damageLocationInfo,
      "coordinates",
      coordinates
    );
  }
}
